import React, { useState } from "react";
import { XMarkIcon, ArrowUpTrayIcon, DocumentIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import toast from "../../utils/notify";

const UploadFileModal = ({
  showUploadFile,
  setShowUploadFile,
  selectedFile,
  setSelectedFile,
  handleUploadFile,
  title = "Upload Document",
  subtitle = "Choose a file to upload",
  acceptedFileTypes = ".pdf,.doc,.docx,.png,.jpg,.jpeg",
  maxFileSize = 3, // in MB
  fileTypeLabel = ["PDF", "DOC", "DOCX", "PNG", "JPG", "JPEG"],
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");

  const validateFile = (file) => {
    setError("");
    if (!file) return true;

    // Validate file type
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    const allowedExtensions = acceptedFileTypes
      .split(",")
      .map((ext) => ext.trim().toLowerCase());

    if (!allowedExtensions.includes(fileExtension)) {
      const errorMsg = `Invalid file type. Only ${fileTypeLabel.join(", ")} files are allowed.`;
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    // Validate file size
    const maxSizeBytes = maxFileSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const errorMsg = `File size exceeds ${maxFileSize}MB limit. Your file is ${formatFileSize(file.size)}.`;
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }
    return true;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      } else {
        setSelectedFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!validateFile(selectedFile)) {
      return;
    }
    setIsLoading(true);
    try {
      await handleUploadFile();
      setShowUploadFile(false);
      setSelectedFile(null);
      setError("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {showUploadFile && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-xl shadow-2xl dark:shadow-2xl relative overflow-hidden transform transition-all duration-300 animate-slideUp">

            {/* Header */}
            {/* The app theme colour (set in Platform Settings); gradient classes are not themed. */}
            <div
              className="px-6 py-5 relative overflow-hidden"
              style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
            >

              <div className="flex items-start justify-between relative z-10">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white mb-1">{title}</h2>
                  <p className="text-slate-300 text-xs leading-relaxed">
                    {subtitle}
                  </p>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowUploadFile(false);
                    setSelectedFile(null);
                  }}
                  className="text-slate-300 hover:text-white transition-colors duration-200 p-1.5 rounded-lg ml-3 flex-shrink-0"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 dark:bg-gray-900">
              <form className="space-y-4">

                {/* File Upload Area */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">
                    Document
                  </label>

                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-xl px-4 py-8 transition-all duration-300 cursor-pointer group ${dragActive
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                      : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                      }`}
                  >
                    <input
                      type="file"
                      accept={acceptedFileTypes}
                      title=""
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file && validateFile(file)) {
                          setSelectedFile(file);
                        } else if (file) {
                          setSelectedFile(null);
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />

                    <div className="text-center">
                      {selectedFile ? (
                        <div className="space-y-2">
                          <div className="flex justify-center">
                            <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                              <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                            </div>
                          </div>
                          <div>
                            <p className="text-green-700 dark:text-green-400 font-semibold text-xs">{selectedFile.name}</p>
                            <p className="text-green-600 dark:text-green-500 text-xs mt-0.5">{formatFileSize(selectedFile.size)}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex justify-center">
                            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full group-hover:scale-110 transition-transform duration-200">
                              <ArrowUpTrayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                          </div>
                          <div>
                            <p className="text-gray-900 dark:text-white font-semibold text-xs">Drag and drop your file</p>
                            <p className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">or click to browse</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* File Format Info */}
                  {error && (
                    <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                      <p className="text-xs text-red-700 dark:text-red-400 font-medium">{error}</p>
                    </div>
                  )}

                  <div className="mt-3 p-3 bg-slate-50 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700">
                    <p className="text-xs text-slate-700 dark:text-gray-300 font-medium mb-2">Accepted formats:</p>
                    <div className="flex flex-wrap gap-1">
                      {fileTypeLabel.map((format) => (
                        <span key={format} className="inline-block px-2 py-1 bg-white dark:bg-gray-700 border border-slate-300 dark:border-gray-600 rounded-full text-xs text-slate-600 dark:text-gray-300 font-medium">
                          {format}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-gray-400 mt-2">Maximum file size: {maxFileSize}MB</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={!selectedFile || isLoading || !!error}
                    className={`w-full font-semibold py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm ${selectedFile && !isLoading && !error
                      ? "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 cursor-pointer"
                      : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      }`}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <ArrowUpTrayIcon className="w-4 h-4" />
                        <span>Upload Document</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadFile(false);
                      setSelectedFile(null);
                    }}
                    className="w-full font-semibold py-2.5 px-4 rounded-lg bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-600 transition-all duration-200 border border-slate-300 dark:border-gray-600 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadFileModal;
